import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  TrendingUp, TrendingDown, Users, RefreshCw, Sparkles, AlertCircle,
  DollarSign, Target, Globe, BarChart3, MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------------------------------------------------------------------------
// WhatsApp win-back helper
// Normalises a phone number to Indian wa.me format (91XXXXXXXXXX) and builds a
// pre-filled message URL. Returns null if the number is unusable.
// ---------------------------------------------------------------------------
const buildWhatsAppLink = (rawPhone, message) => {
  if (!rawPhone) return null;
  // Strip everything except digits
  let digits = String(rawPhone).replace(/\D/g, '');
  if (!digits) return null;
  // Drop a leading 0 (Indian local format)
  if (digits.startsWith('0')) digits = digits.slice(1);
  // If 10 digits, prefix country code
  if (digits.length === 10) digits = `91${digits}`;
  // Sanity range — wa.me expects 10-15 digit international number
  if (digits.length < 10 || digits.length > 15) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

const monthYearShort = (iso) => {
  if (!iso) return 'a while ago';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  } catch {
    return 'a while ago';
  }
};

const lapsedWinbackMessage = (customer) => {
  const name = (customer.name || 'there').split(' ')[0]; // first name only
  const last = monthYearShort(customer.last_purchase_at);
  return (
    `Hi ${name}! 👋\n\n` +
    `This is ANI, an AI agent from Upper Circuit Stocks. I noticed you've been part of our community since ${last} ` +
    `but haven't subscribed recently — just wanted to personally check in.\n\n` +
    `As a special gesture for our returning students, we're extending a limited *10% OFF* on any plan ` +
    `(monthly or quarterly). Just reply with the service you'd like, and I'll share the upgraded link directly.\n\n` +
    `Markets are giving great setups right now — would love to have you back. 📈\n\n` +
    `— ANI (AI Assistant)\nUpper Circuit Stocks\nuppercircuitstocks.com`
  );
};

const inactiveOneTimeMessage = (customer) => {
  const name = (customer.name || 'there').split(' ')[0];
  const last = monthYearShort(customer.last_purchase_at);
  return (
    `Hi ${name}! 👋\n\n` +
    `This is ANI, an AI agent from Upper Circuit Stocks. You took our service back in ${last} — I hope it helped ` +
    `you trade better. Wanted to reach out and ask: would you give us another chance?\n\n` +
    `For our past students, we're offering an exclusive *10% OFF* on any plan + a *free 1-on-1 strategy ` +
    `session* with Chirag. The markets have changed a lot since you were last with us — there are new setups ` +
    `you'd love.\n\n` +
    `Just reply "YES" and I'll share the personalized link. 📈\n\n` +
    `— ANI (AI Assistant)\nUpper Circuit Stocks\nuppercircuitstocks.com`
  );
};

const dropoffRecoveryMessage = (row) => {
  const name = (row.user_name || 'there').split(' ')[0];
  const svc = (row.subscription_type || 'our plan').replace(/_/g, ' ').replace(/3month/i, '3-month');
  const amount = Number(row.final_amount || row.amount_paid || 0);
  return (
    `Hi ${name}! 👋\n\n` +
    `This is ANI, an AI agent from Upper Circuit Stocks. I saw you started checking out *${svc}* ` +
    (amount ? `(₹${amount.toLocaleString('en-IN')}) ` : '') +
    `but the payment didn't go through — wanted to make sure everything is OK at your end.\n\n` +
    `If you faced any issue (UPI failure / card declined / second thoughts), just reply here and ` +
    `our team will personally help. As a one-time gesture for unfinished checkouts, you can use code *COMEBACK10* ` +
    `for an extra 10% off — valid 48 hours.\n\n` +
    `— ANI (AI Assistant)\nUpper Circuit Stocks\nuppercircuitstocks.com`
  );
};

const WhatsAppButton = ({ phone, message, label = 'WhatsApp', size = 'xs' }) => {
  const url = buildWhatsAppLink(phone, message);
  if (!url) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-gray-100 text-gray-400 cursor-not-allowed"
        title="No valid phone number on file"
      >
        <MessageCircle className="w-3 h-3" /> No phone
      </span>
    );
  }
  const cls = size === 'xs'
    ? 'inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded bg-green-600 hover:bg-green-700 text-white transition-colors'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded bg-green-600 hover:bg-green-700 text-white transition-colors';
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cls}
      title="Open WhatsApp with pre-filled message"
      data-testid="winback-whatsapp-btn"
    >
      <MessageCircle className={size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {label}
    </a>
  );
};

const StatPill = ({ label, value, hint, tone = 'neutral', icon: Icon }) => {
  const toneCls = {
    good: 'bg-green-50 border-green-200 text-green-800',
    bad: 'bg-red-50 border-red-200 text-red-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    neutral: 'bg-gray-50 border-gray-200 text-gray-800',
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${toneCls}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="text-xl font-bold">{value}</div>
      {hint && <div className="text-xs opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
};

const fmtINR = (n) => {
  if (n == null || isNaN(n)) return '₹0';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const AIInsightsTab = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');

  const fetchInsights = async (forceRefresh = false) => {
    setError('');
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    try {
      const url = forceRefresh
        ? `${API_URL}/api/admin/ai-insights/refresh?days=${days}`
        : `${API_URL}/api/admin/ai-insights?days=${days}&_t=${Date.now()}`;
      const r = await fetch(url, {
        method: forceRefresh ? 'POST' : 'GET',
        cache: 'no-store',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setInsights(data);
    } catch (e) {
      setError(`Failed to load insights: ${e.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchInsights(); /* eslint-disable-next-line */ }, [days]);

  if (loading && !insights) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Sparkles className="w-8 h-8 mx-auto animate-pulse text-purple-600 mb-3" />
          <p className="text-gray-600">Crunching numbers & calling Claude…</p>
          <p className="text-xs text-gray-400 mt-1">First load may take ~10s. Cached for 30 min after.</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-red-600">
          <AlertCircle className="w-6 h-6 mx-auto mb-2" />
          <p>{error}</p>
          <Button onClick={() => fetchInsights(true)} className="mt-3" size="sm">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (!insights) return null;

  const { customers, funnel, traffic_sources: traffic, revenue, cohort_retention, coupon_effectiveness, ai_summary } = insights;

  return (
    <div className="space-y-6" data-testid="ai-insights-tab">
      {/* Header & controls */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Sparkles className="w-5 h-5 text-purple-600" />
                AI Business Insights
              </CardTitle>
              <CardDescription>
                Cohort retention · repeat customers · drop-off funnel · traffic ROI · AI-generated growth playbook
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
                data-testid="insights-window-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
                <option value={180}>Last 6 months</option>
              </select>
              <Button
                onClick={() => fetchInsights(true)}
                disabled={refreshing}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="insights-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing…' : 'Refresh AI Summary'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* AI Summary - the headline value */}
      <Card className="border-2 border-purple-200 bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Claude's Growth Playbook
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap"
            data-testid="insights-ai-summary"
          >
            {ai_summary || 'No summary generated yet.'}
          </div>
        </CardContent>
      </Card>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Unique Customers" value={customers.total_unique_customers} icon={Users} />
        <StatPill
          label="Repeat Rate"
          value={`${customers.repeat_rate_pct}%`}
          hint={`${customers.repeat_customers_count} of ${customers.total_unique_customers}`}
          tone={customers.repeat_rate_pct >= 20 ? 'good' : customers.repeat_rate_pct >= 10 ? 'warn' : 'bad'}
          icon={Target}
        />
        <StatPill
          label="Lapsed (45d+)"
          value={customers.lapsed_customers_count}
          hint="Win-back candidates"
          tone={customers.lapsed_customers_count > 0 ? 'warn' : 'good'}
          icon={TrendingDown}
        />
        <StatPill
          label={`Conversion (${funnel.window_days}d)`}
          value={`${funnel.overall_conversion_pct}%`}
          hint={`${funnel.completed_payments} of ${funnel.visitors} visitors`}
          tone={funnel.overall_conversion_pct >= 2 ? 'good' : funnel.overall_conversion_pct >= 1 ? 'warn' : 'bad'}
          icon={TrendingUp}
        />
      </div>

      {/* Funnel */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Drop-off Funnel · Last {funnel.window_days} days
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <StatPill label="Visitors" value={funnel.visitors} hint="Unique sessions tracked" />
            <StatPill
              label="Started Checkout"
              value={funnel.initiated_checkout}
              hint={`${funnel.visit_to_initiate_pct}% of visitors`}
              tone={funnel.visit_to_initiate_pct >= 3 ? 'good' : 'warn'}
            />
            <StatPill
              label="Completed Payment"
              value={funnel.completed_payments}
              hint={`${funnel.initiate_to_complete_pct}% of starters`}
              tone={funnel.initiate_to_complete_pct >= 50 ? 'good' : 'bad'}
            />
          </div>
          {funnel.pending_or_failed > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
              <strong>{funnel.pending_or_failed} drop-offs in window.</strong>{' '}
              These customers started checkout but never completed payment — prime for a WhatsApp follow-up.
            </div>
          )}
          {funnel.recent_dropoffs && funnel.recent_dropoffs.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold mb-2 text-gray-700">Recent drop-offs (top 10)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left p-2">Customer</th>
                      <th className="text-left p-2">Service</th>
                      <th className="text-left p-2">Amount</th>
                      <th className="text-left p-2">Source</th>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnel.recent_dropoffs.slice(0, 10).map((d, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="p-2">
                          <div className="font-medium">{d.user_name || '—'}</div>
                          <div className="text-gray-500">{d.user_phone || d.user_email || ''}</div>
                        </td>
                        <td className="p-2"><Badge variant="outline">{d.subscription_type || '—'}</Badge></td>
                        <td className="p-2 font-medium">{fmtINR(d.final_amount || d.amount_paid)}</td>
                        <td className="p-2 text-gray-600">{d.utm_source || 'direct'}</td>
                        <td className="p-2 text-gray-500">
                          {d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Traffic Sources */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-emerald-600" />
            Traffic Source ROI
          </CardTitle>
          <CardDescription className="text-xs">Where to double-down · sorted by revenue</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {traffic.sources.length === 0 && (
              <p className="text-sm text-gray-500">No tracked visitors yet in this window. Make sure UTM tags are on outbound links.</p>
            )}
            {traffic.sources.map((s, i) => {
              const max = Math.max(...traffic.sources.map(x => x.visitors), 1);
              const pct = (s.visitors / max) * 100;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-24 text-xs font-medium text-gray-700 truncate" title={s.source}>{s.source}</div>
                  <div className="flex-1 h-7 bg-gray-100 rounded-md relative overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-gray-800">
                      {s.visitors} visitors · {s.paid} paid · {fmtINR(s.revenue)} · {s.conversion_pct}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Repeat customers + lapsed (win-back) + inactive one-timers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">🏆 Top Repeat Customers</CardTitle>
            <CardDescription className="text-xs">Highest LTV — your VIPs</CardDescription>
          </CardHeader>
          <CardContent>
            {customers.top_repeat.length === 0 ? (
              <p className="text-sm text-gray-500">No repeat customers yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Purchases</th>
                    <th className="text-left p-2">LTV</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.top_repeat.slice(0, 8).map((c, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2">
                        <div className="font-medium">{c.name || '—'}</div>
                        <div className="text-gray-500">{c.phone || c.email}</div>
                      </td>
                      <td className="p-2">{c.purchase_count}</td>
                      <td className="p-2 font-bold text-green-700">{fmtINR(c.total_ltv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">😴 Lapsed Customers · Win-back</CardTitle>
            <CardDescription className="text-xs">Bought multiple times before, gone quiet 45+ days — message them</CardDescription>
          </CardHeader>
          <CardContent>
            {customers.top_lapsed.length === 0 ? (
              <p className="text-sm text-gray-500">No multi-purchase customers in this window.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Bought</th>
                    <th className="text-left p-2">Last</th>
                    <th className="text-left p-2">Win-back</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.top_lapsed.slice(0, 8).map((c, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2">
                        <div className="font-medium">{c.name || '—'}</div>
                        <div className="text-gray-500">{c.phone || c.email}</div>
                      </td>
                      <td className="p-2">{c.purchase_count}× · {fmtINR(c.total_ltv)}</td>
                      <td className="p-2 text-gray-500">
                        {c.last_purchase_at ? new Date(c.last_purchase_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                      </td>
                      <td className="p-2">
                        <WhatsAppButton phone={c.phone} message={lapsedWinbackMessage(c)} label="Win back" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inactive One-Time Buyers — the BIGGEST win-back pool for most subscription businesses */}
      <Card className="border-l-4 border-l-amber-500">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            💤 Inactive One-Time Buyers · The Big Win-back Pool
          </CardTitle>
          <CardDescription className="text-xs">
            People who bought once, 45+ days ago, never returned. <strong>Often your single biggest revenue lever.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!customers.top_inactive_one_time || customers.top_inactive_one_time.length === 0 ? (
            <p className="text-sm text-gray-500">No inactive one-time customers — everyone is fresh!</p>
          ) : (
            <>
              <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                <strong>{customers.inactive_one_time_count} customers</strong> bought once and never came back.
                Even a 10% reactivation = significant revenue. Pre-filled message offers <strong>40% OFF + free 1-on-1</strong>.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left p-2">Customer</th>
                      <th className="text-left p-2">Phone / Email</th>
                      <th className="text-left p-2">Spent</th>
                      <th className="text-left p-2">Last Active</th>
                      <th className="text-left p-2">Reactivate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.top_inactive_one_time.slice(0, 15).map((c, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="p-2 font-medium">{c.name || '—'}</td>
                        <td className="p-2 text-gray-500">{c.phone || c.email || '—'}</td>
                        <td className="p-2 font-semibold text-gray-700">{fmtINR(c.total_ltv)}</td>
                        <td className="p-2 text-gray-500">
                          {c.last_purchase_at ? new Date(c.last_purchase_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                        </td>
                        <td className="p-2">
                          <WhatsAppButton phone={c.phone} message={inactiveOneTimeMessage(c)} label="Reactivate" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Revenue + Cohort */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Revenue Mix
            </CardTitle>
            <CardDescription className="text-xs">
              Total: {fmtINR(revenue.total_revenue)} · MoM:{' '}
              {revenue.mom_growth_pct == null ? '—' : (
                <span className={revenue.mom_growth_pct >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {revenue.mom_growth_pct >= 0 ? '+' : ''}{revenue.mom_growth_pct}%
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {revenue.by_service.length === 0 ? (
              <p className="text-sm text-gray-500">No completed revenue in this window.</p>
            ) : (
              <div className="space-y-2">
                {revenue.by_service.map((s, i) => {
                  const max = revenue.by_service[0].revenue || 1;
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="font-medium">{s.service}</span>
                        <span className="text-gray-700">{fmtINR(s.revenue)} · {s.count} sales · {fmtINR(s.avg_ticket)} avg</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-400 to-emerald-600"
                             style={{ width: `${(s.revenue / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">📈 Cohort Retention</CardTitle>
            <CardDescription className="text-xs">% of customers from each signup month who bought again</CardDescription>
          </CardHeader>
          <CardContent>
            {cohort_retention.cohorts.length === 0 ? (
              <p className="text-sm text-gray-500">Not enough history yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left p-2">Cohort</th>
                    <th className="text-left p-2">Size</th>
                    <th className="text-left p-2">Avg LTV</th>
                    <th className="text-left p-2">30d</th>
                    <th className="text-left p-2">60d</th>
                    <th className="text-left p-2">90d</th>
                  </tr>
                </thead>
                <tbody>
                  {cohort_retention.cohorts.map((c, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2 font-medium">{c.cohort_month}</td>
                      <td className="p-2">{c.size}</td>
                      <td className="p-2">{fmtINR(c.avg_ltv)}</td>
                      <td className="p-2">{c.retention_30d_pct}%</td>
                      <td className="p-2">{c.retention_60d_pct}%</td>
                      <td className="p-2">{c.retention_90d_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Coupon effectiveness */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">🎁 Coupon Effectiveness</CardTitle>
          <CardDescription className="text-xs">Revenue each code has actually driven</CardDescription>
        </CardHeader>
        <CardContent>
          {coupon_effectiveness.coupons.length === 0 ? (
            <p className="text-sm text-gray-500">No coupons created yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Uses</th>
                  <th className="text-left p-2">Revenue</th>
                  <th className="text-left p-2">Discount Given</th>
                  <th className="text-left p-2">Slots Left</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {coupon_effectiveness.coupons.map((c, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="p-2 font-mono font-semibold">{c.code}</td>
                    <td className="p-2">{c.uses}</td>
                    <td className="p-2 text-green-700 font-semibold">{fmtINR(c.revenue_generated)}</td>
                    <td className="p-2 text-orange-700">-{fmtINR(c.discount_given)}</td>
                    <td className="p-2">{c.remaining_slots == null ? '∞' : c.remaining_slots}</td>
                    <td className="p-2">
                      <Badge variant={c.status === 'active' ? 'default' : 'outline'}>
                        {c.status || 'active'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-gray-400 text-center">
        Data refreshes every 30 min · Last computed: {new Date(insights.generated_at).toLocaleString('en-IN')}
      </div>
    </div>
  );
};

export default AIInsightsTab;
